import { useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table'
import { ExternalLink, ArrowUpDown } from 'lucide-react'
import type { Candidate } from '@/lib/types'

const columnHelper = createColumnHelper<Candidate>()

// Score bar visualization component
function ScoreBar({ score, tooltip }: { score: number; tooltip?: string }) {
  const color =
    score >= 70
      ? 'bg-green-500'
      : score >= 50
      ? 'bg-yellow-500'
      : 'bg-red-500'

  const textColor =
    score >= 70
      ? 'text-green-400'
      : score >= 50
      ? 'text-yellow-400'
      : 'text-red-400'

  return (
    <div className="group relative flex items-center gap-2 min-w-[78px]">
      <div className="flex-1 h-1 bg-slate-800 overflow-hidden">
        <div
          className={`h-full ${color} transition-all`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className={`font-mono text-xs font-medium ${textColor} w-7 text-right`}>
        {score}
      </span>
      {tooltip && (
        <div className="pointer-events-none absolute left-0 top-6 z-30 hidden w-80 rounded-md border border-slate-700 bg-slate-950 p-3 text-xs leading-relaxed text-slate-200 shadow-xl group-hover:block">
          {tooltip}
        </div>
      )}
    </div>
  )
}

function toPercentScore(value: number | null | undefined): number {
  if (value == null || Number.isNaN(value)) return 0
  return value <= 1 ? Math.round(value * 100) : Math.round(value)
}

function reasoningText(row: Candidate) {
  if (Array.isArray(row.reasoning)) return row.reasoning.join(' ')
  return row.reasoning || row.explanation || ''
}

const columns: ColumnDef<Candidate, any>[] = [
  columnHelper.accessor('activity_status', {
    header: 'Activity',
    cell: info => { const status = info.getValue() || 'unchecked'; return <span title={info.row.original.activity_reason} className={`rounded px-2 py-1 text-xs ${status === 'active' ? 'bg-green-100 text-green-800' : status === 'inactive' ? 'bg-slate-200 text-slate-700' : 'bg-amber-50 text-amber-800'}`}>{status === 'error' ? 'Unknown (failed)' : status === 'unchecked' ? 'Unknown (unchecked)' : status}</span> },
  }),
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
  }),
  columnHelper.accessor('title', {
    header: 'Title',
    cell: (info) => (
      <span className="text-sm text-slate-400 max-w-[260px] truncate block">
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor('location', {
    header: 'Location',
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),
  columnHelper.accessor('network_distance', {
    header: 'Connection',
    cell: (info) => {
      const distance = info.getValue()
      if (distance == null) return <span className="text-xs text-slate-500">-</span>

      // Format connection levels to be user-friendly
      const formatDistance = (dist: string | number) => {
        if (typeof dist === 'number') return `${dist}°`
        const str = String(dist).toUpperCase()
        if (str === 'FIRST_DEGREE' || str === '1') return '1°'
        if (str === 'SECOND_DEGREE' || str === '2') return '2°'
        if (str === 'THIRD_DEGREE' || str === '3') return '3°'
        if (str.includes('1ST')) return '1°'
        if (str.includes('2ND')) return '2°'
        if (str.includes('3RD')) return '3°'
        return str
      }

      return (
        <span className="text-xs font-medium text-blue-400">
          {formatDistance(distance)}
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => row.followers || row.followers_count, {
    id: 'followers',
    header: 'Followers',
    cell: (info) => {
      const count = info.getValue()
      return (
        <span className="font-mono text-sm">
          {count != null ? count.toLocaleString() : '-'}
        </span>
      )
    },
  }),
  columnHelper.accessor('intent_match', {
    header: 'Intent',
    cell: (info) => <ScoreBar score={toPercentScore(info.getValue())} tooltip={reasoningText(info.row.original)} />,
  }),
  columnHelper.accessor('post_relevance', {
    header: 'Post',
    cell: (info) => {
      const score = info.getValue()
      if (score == null) return <span className="text-xs text-slate-500">-</span>
      return <ScoreBar score={score} />
    },
  }),
  columnHelper.accessor('baseline_score', {
    header: 'Baseline',
    cell: (info) => <ScoreBar score={info.getValue()} />,
  }),
  columnHelper.accessor('final_score', {
    header: 'Final',
    cell: (info) => <ScoreBar score={info.getValue()} />,
  }),
  columnHelper.accessor('source', {
    header: 'Source',
    cell: (info) => {
      const source = info.getValue()
      return (
        <span
          className={
            source === 'engage'
              ? 'px-1.5 py-0.5 rounded text-xs bg-slate-800 text-slate-200'
              : 'px-1.5 py-0.5 rounded text-xs bg-slate-900 text-slate-300'
          }
        >
          {source === 'engage' ? 'Engager' : 'Sales Nav'}
        </span>
      )
    },
  }),
  columnHelper.accessor('url', {
    header: 'Profile',
    cell: (info) => {
      const url = info.getValue()
      if (!url) return null
      // Ensure URL has protocol prefix for proper external linking
      const href = url.startsWith('http') ? url : `https://${url}`
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
        >
          <ExternalLink className="w-3 h-3" />
        </a>
      )
    },
  }),
]

interface GrowthListTableProps {
  candidates: Candidate[]
}

export function GrowthListTable({ candidates }: GrowthListTableProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'final_score', desc: true }
  ])

  const table = useReactTable({
    data: candidates,
    columns,
    columnResizeMode: 'onChange',
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  if (candidates.length === 0) {
    return (
      <div className="p-8 text-center text-slate-500">
        <p className="mb-2">No profiles match the current list filter</p>
        <p className="text-sm">
          Try All activity or adjust the list filters
        </p>
      </div>
    )
  }

  return (
    <div className="max-w-full overflow-auto rounded-md border border-slate-800">
      <table className="min-w-[980px] w-full text-sm">
        <thead className="bg-slate-900 border-b border-slate-800">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="relative px-3 py-2 text-left text-xs font-medium text-slate-400"
                  style={{ width: header.getSize() }}
                >
                  {header.column.getCanSort() ? (
                    <button
                      onClick={header.column.getToggleSortingHandler()}
                      className="flex items-center gap-1 hover:text-slate-200 transition-colors"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <ArrowUpDown className="h-3 w-3" />
                    </button>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                  <button
                    type="button"
                    aria-label="Resize column"
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-slate-700/0 hover:bg-slate-500"
                  />
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="divide-y divide-slate-800">
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="hover:bg-slate-900">
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-3 py-2 align-top" style={{ width: cell.column.getSize() }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
